/**
 * Transition type definitions for Image and Video overlay animations
 */

// ------------------ Enums ------------------

/**
 * Types of transitions available for overlays
 */
export enum TransitionType {
  FADE = 'FADE',
  MOVE = 'MOVE',
  ROTATE = 'ROTATE',
  SCALE = 'SCALE',
  DROP = 'DROP'
}

/**
 * Lifecycle phase for transitions
 */
export enum TransitionLifeCycleType {
  IN = 'IN',
  OUT = 'OUT'
}

/**
 * Direction options for MOVE transitions
 */
export enum TransitionMoveDirection {
  UP = 'UP',
  DOWN = 'DOWN',
  LEFT = 'LEFT',
  RIGHT = 'RIGHT'
}

/**
 * Direction options for ROTATE transitions
 */
export enum TransitionRotateDirection {
  CLOCKWISE = 'CLOCKWISE',
  ANTI_CLOCKWISE = 'ANTI_CLOCKWISE'
}

// ------------------ Properties Interfaces ------------------

/**
 * Common properties shared by all transitions
 */
export interface CommonTransitionProperties {
  duration: number; // Duration in milliseconds
}

/**
 * Additional properties for MOVE transitions
 */
export interface TransitionMoveProperties extends CommonTransitionProperties {
  moveDirection: TransitionMoveDirection;
}

/**
 * Additional properties for ROTATE transitions
 */
export interface TransitionRotateProperties extends CommonTransitionProperties {
  rotateDirection: TransitionRotateDirection;
}

// ------------------ Transition Interfaces ------------------

/**
 * Base transition interface for simple transitions (FADE, SCALE, DROP)
 */
export interface BaseTransition {
  type: TransitionType.FADE | TransitionType.SCALE | TransitionType.DROP;
  properties: CommonTransitionProperties;
}

/**
 * MOVE transition with direction property
 */
export interface MoveTransition {
  type: TransitionType.MOVE;
  properties: TransitionMoveProperties;
}

/**
 * ROTATE transition with direction property
 */
export interface RotateTransition {
  type: TransitionType.ROTATE;
  properties: TransitionRotateProperties;
}

// ------------------ Union Types ------------------

/**
 * Union type for all lifecycle transitions (used for in/out)
 */
export type LifeCycleTransition = BaseTransition | MoveTransition | RotateTransition;

// ------------------ Main Transition Interface ------------------

/**
 * Main transition interface with optional in/out lifecycle transitions
 */
export interface Transition {
  in?: LifeCycleTransition | null;
  out?: LifeCycleTransition | null;
}
